import React from 'react'
import { defineMessages, IntlShape, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { SESSION_TYPE_MESSAGES } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { CalendarCardAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { BookSessionsIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/BookSessionsIcon'
import { PlusSignIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PlusSignIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { CalendarCardActionMenu } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/calendarCard/CalendarCardActionMenu'
import { DBTTeensSessionTypeIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dbtTeensSessionTypeIcon/DBTTeensSessionTypeIcon'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { buildDBTTeensSessionRows, ProgramAwareSessionRow } from './programAwareSessionsModel'

const ProgramAwareSessionsContainer = styled.View(({ theme }) => ({
  alignSelf: 'center',
  maxWidth: 1280,
  width: '100%',
  paddingHorizontal: theme.spacing['8px'],
}))

const ScheduleSessionContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing['24px'],
}))

const SessionSection = styled.View<{ isPast?: boolean }>(({ theme, isPast }) => ({
  marginTop: isPast ? theme.spacing['32px'] : 0,
}))

const SectionTitle = styled(Title)(({ theme }) => ({
  marginBottom: theme.spacing['12px'],
}))

const SessionRow = styled.View<{ isFacilitatedByAnotherProvider: boolean; isPast: boolean }>(
  ({ theme, isFacilitatedByAnotherProvider, isPast }) => ({
    alignItems: 'center',
    backgroundColor: isFacilitatedByAnotherProvider ? theme.colors.backgroundElement : theme.colors.backgroundPrimary,
    flexDirection: 'row',
    gap: theme.spacing['16px'],
    minHeight: 104,
    padding: theme.spacing['16px'],
    ...(isPast
      ? {
          borderBottomColor: theme.colors.borderDefault,
          borderBottomWidth: 1,
        }
      : {
          borderColor: theme.colors.borderDefault,
          borderRadius: theme.spacing['16px'],
          borderWidth: isFacilitatedByAnotherProvider ? 0 : 1,
          marginBottom: theme.spacing['12px'],
        }),
  }),
)

const SessionDetails = styled.View(({ theme }) => ({
  flex: 1,
  gap: theme.spacing['2px'],
  minWidth: 0,
}))

const MetadataRow = styled.View({
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
})

const ActionMenuContainer = styled.View(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing['16px'],
  marginRight: -8,
}))

const RecordingContainer = styled.View({
  alignItems: 'flex-end',
  flexShrink: 0,
  marginLeft: 16,
})

const EmptyState = styled.View(({ theme }) => ({
  alignItems: 'center',
  borderColor: theme.colors.borderDefault,
  borderRadius: theme.spacing['16px'],
  borderWidth: 1,
  flexDirection: 'row',
  gap: theme.spacing['12px'],
  justifyContent: 'flex-start',
  padding: theme.spacing['24px'],
}))

const EmptyStateIcon = styled.View(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.borderDefault,
  borderRadius: 20,
  height: 40,
  justifyContent: 'center',
  width: 40,
}))

type ProgramAwareSessionsViewProps = {
  authProviderId: string
  canScheduleSession: boolean
  caregiverFirstName: string
  clientFirstName: string
  onCancelSession: (appointment: CalendarCardAppointment) => void
  onRescheduleSession: (appointment: CalendarCardAppointment) => void
  onScheduleSession: () => void
  onViewSessionRecording: (sessionId: string) => void
  pastAppointments: CalendarCardAppointment[]
  upcomingAppointments: CalendarCardAppointment[]
}

const ATTENDANCE_MESSAGES = defineMessages({
  attended: { defaultMessage: 'Attended', description: 'Attendance status for a completed session' },
  canceled: { defaultMessage: 'Canceled', description: 'Attendance status for a canceled session' },
  missed: { defaultMessage: 'Missed', description: 'Attendance status for a missed session' },
})

const DBT_TEENS_SESSION_TYPE_MESSAGES = defineMessages({
  intake: { defaultMessage: 'Intake session', description: 'DBT Teens intake session type label' },
  teen: { defaultMessage: 'Teen session', description: 'DBT Teens individual teen session type label' },
  caregiver: { defaultMessage: 'Caregiver session', description: 'DBT Teens caregiver session type label' },
  family: { defaultMessage: 'Family session', description: 'DBT Teens family session type label' },
  group: { defaultMessage: 'Group session', description: 'DBT Teens group session type label' },
  medication: { defaultMessage: 'Medication session', description: 'DBT Teens medication session type label' },
  unknown: {
    defaultMessage: 'Session',
    description: 'Fallback session type label when a DBT Teens appointment type is unavailable',
  },
})

const FACILITATOR_ROLE_MESSAGES = defineMessages({
  therapist: { defaultMessage: 'therapist', description: 'Therapist facilitator role label' },
  psychiatrist: { defaultMessage: 'psychiatrist', description: 'Psychiatrist facilitator role label' },
  prescriber: { defaultMessage: 'prescriber', description: 'Prescriber facilitator role label' },
  coach: { defaultMessage: 'coach', description: 'Coach facilitator role label' },
})

const FACILITATOR_MESSAGES = defineMessages({
  upcoming: {
    defaultMessage: '{facilitator} is facilitating this session',
    description: 'Upcoming-session message identifying the facilitating provider',
  },
  attended: {
    defaultMessage: '{facilitator} facilitated this session',
    description: 'Past-session message identifying the facilitating provider',
  },
  didNotOccur: {
    defaultMessage: '{facilitator} was scheduled to facilitate this session',
    description: 'Message identifying the provider for a session that did not occur',
  },
})

const getMeetingDetails = (intl: IntlShape, appointment: CalendarCardAppointment): string => {
  const meetingFormatMessage = SESSION_TYPE_MESSAGES[appointment.meetingFormat as keyof typeof SESSION_TYPE_MESSAGES]
  const meetingFormat = meetingFormatMessage ? intl.formatMessage(meetingFormatMessage) : appointment.meetingFormat

  return intl.formatMessage(
    {
      defaultMessage: '{meetingFormat} · {duration} min',
      description: 'Session meeting format and duration',
    },
    { duration: appointment.appointmentDuration, meetingFormat },
  )
}

const ProgramAwareSessionsView: React.FC<ProgramAwareSessionsViewProps> = ({
  authProviderId,
  canScheduleSession,
  caregiverFirstName,
  clientFirstName,
  onCancelSession,
  onRescheduleSession,
  onScheduleSession,
  onViewSessionRecording,
  pastAppointments,
  upcomingAppointments,
}) => {
  const intl = useIntl()
  const { colors } = useTheme()

  const getSessionLabel = (row: ProgramAwareSessionRow) =>
    row.attendance === 'canceled'
      ? undefined
      : intl.formatMessage(
          { defaultMessage: 'Session {sessionNumber}', description: 'Numbered client session label' },
          { sessionNumber: row.appointment.sessionNumber },
        )

  const renderActionMenu = (row: ProgramAwareSessionRow) =>
    row.showActionMenu ? (
      <ActionMenuContainer>
        <CalendarCardActionMenu
          handleCancelPress={onCancelSession}
          handleReschedulePress={onRescheduleSession}
          isTooLateToReschedule={row.isTooLateToReschedule}
          session={row.appointment}
        />
      </ActionMenuContainer>
    ) : null

  const renderRecording = (row: ProgramAwareSessionRow) =>
    row.isPast && row.attendance === 'attended' && !row.isFacilitatedByAnotherProvider ? (
      <RecordingContainer>
        {row.appointment.videoRecording ? (
          <Link
            onPress={() => onViewSessionRecording(row.appointment.videoRecording!)}
            size={BodyTextSize.SMALL}
            text={intl.formatMessage({
              defaultMessage: 'Watch recording',
              description: 'Link to watch a past session recording',
            })}
            underline
          />
        ) : (
          <BodyText color={colors.textInactive} size={BodyTextSize.SMALL}>
            {intl.formatMessage({
              defaultMessage: 'Recording unavailable',
              description: 'Message shown when a past session does not have a recording',
            })}
          </BodyText>
        )}
      </RecordingContainer>
    ) : null

  const renderDBTTeensRow = (row: ProgramAwareSessionRow) => {
    const { appointment } = row
    const facilitatorName =
      row.facilitatorName ??
      intl.formatMessage({
        defaultMessage: 'Another care team provider',
        description: 'Fallback label for a provider facilitating a client session',
      })
    const facilitatorRole = row.facilitatorRole
      ? intl.formatMessage(FACILITATOR_ROLE_MESSAGES[row.facilitatorRole])
      : undefined
    const facilitator = facilitatorRole ? `${facilitatorName} (${facilitatorRole})` : facilitatorName
    const facilitatorMessage =
      !row.isPast && !row.attendance
        ? FACILITATOR_MESSAGES.upcoming
        : row.attendance === 'attended'
          ? FACILITATOR_MESSAGES.attended
          : FACILITATOR_MESSAGES.didNotOccur
    const facilitatorCopy = intl.formatMessage(facilitatorMessage, { facilitator })
    const sessionLabel = getSessionLabel(row)
    const title = row.isFacilitatedByAnotherProvider
      ? [sessionLabel, facilitatorCopy].filter(Boolean).join(' · ')
      : [sessionLabel, row.dateTimeLabel].filter(Boolean).join(' · ')
    const participantNames = row.participantNames.join(', ')
    const sessionMetadata = [intl.formatMessage(DBT_TEENS_SESSION_TYPE_MESSAGES[row.sessionType]), participantNames]
      .filter(Boolean)
      .join(' · ')
    const attendanceColor = row.attendance === 'attended' ? colors.textSuccess : colors.textWarning
    const primaryTextColor = row.isFacilitatedByAnotherProvider ? colors.textInactive : colors.textPrimary
    const secondaryTextColor = row.isFacilitatedByAnotherProvider ? colors.textInactive : colors.textSecondary
    const rowType = row.isPast ? 'past' : 'upcoming'

    return (
      <SessionRow
        key={appointment.appointmentId}
        isFacilitatedByAnotherProvider={row.isFacilitatedByAnotherProvider}
        isPast={row.isPast}
        testID={tID(`ProgramAwareSessions-${rowType}-row-${appointment.appointmentId}`)}
      >
        <DBTTeensSessionTypeIcon sessionType={row.ltSessionType} />
        <SessionDetails>
          <BodyText color={primaryTextColor} medium wrap>
            {title}
          </BodyText>
          {row.isFacilitatedByAnotherProvider && (
            <BodyText color={secondaryTextColor} size={BodyTextSize.SMALL}>
              {row.dateTimeLabel}
            </BodyText>
          )}
          <MetadataRow>
            {row.attendance && (
              <BodyText color={attendanceColor} size={BodyTextSize.SMALL}>
                {`${intl.formatMessage(ATTENDANCE_MESSAGES[row.attendance])} · `}
              </BodyText>
            )}
            <BodyText color={secondaryTextColor} size={BodyTextSize.SMALL} wrap>
              {sessionMetadata}
            </BodyText>
          </MetadataRow>
          <BodyText color={secondaryTextColor} size={BodyTextSize.SMALL}>
            {getMeetingDetails(intl, appointment)}
          </BodyText>
        </SessionDetails>
        {renderActionMenu(row)}
        {renderRecording(row)}
      </SessionRow>
    )
  }

  const renderRows = (appointments: CalendarCardAppointment[], isPast: boolean) =>
    buildDBTTeensSessionRows({
      appointments,
      authProviderId,
      caregiverFirstName,
      clientFirstName,
      isPast,
    }).map(renderDBTTeensRow)

  return (
    <ProgramAwareSessionsContainer>
      {canScheduleSession && (
        <ScheduleSessionContainer>
          <PrimaryButton
            text={intl.formatMessage({
              defaultMessage: 'Schedule session',
              description: 'Button to schedule a new client session',
            })}
            onPress={onScheduleSession}
            testID={tID('Sessions-new-session')}
            leftIcon={<PlusSignIcon size={16} />}
          />
        </ScheduleSessionContainer>
      )}
      <SessionSection testID={tID('ProgramAwareSessions-upcoming')}>
        <SectionTitle size={TitleSize.SMALL}>
          {intl.formatMessage({
            defaultMessage: 'Upcoming sessions',
            description: 'Title for sessions coming up in the future',
          })}
        </SectionTitle>
        {upcomingAppointments.length > 0 ? (
          renderRows(upcomingAppointments, false)
        ) : (
          <EmptyState testID={tID('ProgramAwareSessions-upcoming-empty-state')}>
            <EmptyStateIcon>
              <BookSessionsIcon size={20} fillColor={colors.iconDefault} />
            </EmptyStateIcon>
            <BodyText color={colors.textSecondary}>
              {intl.formatMessage({
                defaultMessage: 'No upcoming sessions.',
                description: 'Message displayed when a client has no future sessions scheduled',
              })}
            </BodyText>
          </EmptyState>
        )}
      </SessionSection>
      {pastAppointments.length > 0 && (
        <SessionSection isPast testID={tID('ProgramAwareSessions-past')}>
          <SectionTitle size={TitleSize.SMALL}>
            {intl.formatMessage({
              defaultMessage: 'Past sessions',
              description: 'Title for sessions that happened in the past',
            })}
          </SectionTitle>
          {renderRows(pastAppointments, true)}
        </SessionSection>
      )}
    </ProgramAwareSessionsContainer>
  )
}

export default ProgramAwareSessionsView
